// ===== HOOK: LÓGICA DO CHAT =====

import { useState, useCallback } from "react";
import { Message, DashboardData } from "../types";
import { useN8NIntegration } from "./useN8NIntegration";
import { detectIntent, mapIntentToQueryType } from "../utils/intentDetector";
import { extractTextFromPDF, isValidPDF } from "../utils/fileUtils";
import { INITIAL_MESSAGE, ERROR_MESSAGES, LOADING_MESSAGE } from "../utils/constants";

/**
 * Hook para gerenciar a lógica do chat
 */
export const useChat = (
  userId: string,
  userToken: string,
  dashboardData: DashboardData | null,
  alunosRiscoData: any[],
  turmasData: any[]
) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      ...INITIAL_MESSAGE,
      id: Date.now().toString(),
      timestamp: new Date(),
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const { callN8NWebhook } = useN8NIntegration();

  // ===== PROCESSAR MENSAGEM DO USUÁRIO =====
  const processUserMessage = useCallback(
    async (userInput: string, file?: File) => {
      if (!userInput.trim() && !file) return;

      // Adicionar mensagem do usuário ao histórico
      const userMessage: Message = {
        id: Date.now().toString(),
        role: "user",
        content: file
          ? `${userInput}\n\n📎 Arquivo: ${file.name}`
          : userInput,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setLoading(true);

      try {
        let responseContent = "";

        // CASO 1: Análise de PDF
        if (file && isValidPDF(file)) {
          try {
            const pdfContent = await extractTextFromPDF(file);
            // Passa PDF para N8N analisar
            responseContent = await callN8NWebhook(
              `Por favor, analise este PDF: ${file.name}\n\nConteúdo:\n${pdfContent}`,
              userId,
              userToken,
              "analisar_pdf"
            );
          } catch (error) {
            responseContent = ERROR_MESSAGES.pdfAnalysisError;
          }
        }
        // CASO 2: TODAS as mensagens de texto passam por N8N
        else {
          const intent = detectIntent(userInput.toLowerCase());
          const queryType = mapIntentToQueryType(intent);

          console.log(`📤 Enviando para N8N com queryType: ${queryType}`);

          // Chama webhook N8N com a mensagem
          responseContent = await callN8NWebhook(
            userInput,
            userId,
            userToken,
            queryType
          );
        }

        // Adicionar resposta do assistente
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: responseContent,
          timestamp: new Date(),
          metadata: {
            pdfAnalyzed: !!file,
          },
        };

        setMessages((prev) => [...prev, assistantMessage]);
      } catch (error) {
        console.error("Erro ao processar mensagem:", error);

        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: ERROR_MESSAGES.processingError,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        setLoading(false);
        setUploadedFile(null);
      }
    },
    [userId, userToken, callN8NWebhook]
  );

  // ===== HANDLE FILE UPLOAD =====
  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file && isValidPDF(file)) {
        setUploadedFile(file);
      }
    },
    []
  );

  // ===== REMOVER ARQUIVO =====
  const handleRemoveFile = useCallback(() => {
    setUploadedFile(null);
  }, []);

  // ===== LIMPAR HISTÓRICO =====
  const clearHistory = useCallback(() => {
    setMessages([
      {
        ...INITIAL_MESSAGE,
        id: Date.now().toString(),
        timestamp: new Date(),
      },
    ]);
  }, []);

  return {
    messages,
    loading,
    uploadedFile,
    processUserMessage,
    handleFileUpload,
    handleRemoveFile,
    clearHistory,
  };
};
